import React from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FormattedMessage } from './reactIntl';
import Icon from '../components/Icon/Icon';
import css from './notification.module.css';

const NOTIFICATION_POSITION_TOP_LEFT = 'top-left';
const NOTIFICATION_POSITION_TOP_CENTER = 'top-center';
const NOTIFICATION_POSITION_TOP_RIGHT = 'top-right';
const NOTIFICATION_POSITION_BOTTOM_LEFT = 'bottom-left';
const NOTIFICATION_POSITION_BOTTOM_CENTER = 'bottom-center';
const NOTIFICATION_POSITION_BOTTOM_RIGHT = 'bottom-right';

const NOTIFICATION_DELAY = 5000;

// The Components board's toast: dark, the same in both families. The colours come from the
// design tokens through notification.module.css. Read more: https://react-hot-toast.com/docs/styling
const notificationSettings = {
  position: NOTIFICATION_POSITION_BOTTOM_CENTER,
  toastOptions: {
    duration: NOTIFICATION_DELAY,
    className: css.toast,
    success: {
      icon: <Icon name="check" className={css.iconSuccess} />,
    },
    error: {
      icon: <Icon name="error" className={css.iconError} />,
    },
  },
};

// An error interrupts screen readers (role="alert"); the others wait their turn (role="status",
// react-hot-toast's default). Per-type ariaProps in toastOptions don't reach the toast, so the
// error function sets them. Everything else is react-hot-toast's `toast`.
const ERROR_ARIA_PROPS = { role: 'alert', 'aria-live': 'assertive' };
const notification = Object.assign((message, options) => toast(message, options), toast, {
  error: (message, options) => toast.error(message, { ariaProps: ERROR_ARIA_PROPS, ...options }),
});

/**
 * A success toast with an Undo action, as on the board (a tick, "Link copied", Undo). Undo closes the
 * toast and calls `onUndo`. Returns the toast id.
 *
 *   notificationWithUndo(intl.formatMessage({ id: 'X.removed' }), () => dispatch(restore(id)));
 */
const notificationWithUndo = (message, onUndo, options) =>
  toast.success(
    t => (
      <span className={css.withAction}>
        <span>{message}</span>
        <button
          type="button"
          className={css.undo}
          onClick={() => {
            toast.dismiss(t.id);
            onUndo();
          }}
        >
          <FormattedMessage id="Notification.undo" />
        </button>
      </span>
    ),
    options
  );

export {
  // provider
  Toaster as NotificationProvider,
  // notification alert fn
  notification,
  // a success notification with an Undo action
  notificationWithUndo,
  // notification settings
  notificationSettings,
  // notification positions
  NOTIFICATION_POSITION_TOP_LEFT,
  NOTIFICATION_POSITION_TOP_CENTER,
  NOTIFICATION_POSITION_TOP_RIGHT,
  NOTIFICATION_POSITION_BOTTOM_LEFT,
  NOTIFICATION_POSITION_BOTTOM_CENTER,
  NOTIFICATION_POSITION_BOTTOM_RIGHT,
};
